import { Users, UserPlus, TrendingUp, Calendar } from 'lucide-react';
import { founderAnalyticsService } from '../../lib/founder-analytics-service';
import { comparisonUtils } from '../../lib/comparison-utils';

export function UsersTab({ data }: { data: any }) {
  if (!data || !data.userGrowth) {
    return (
      <div className="text-center py-12">
        <p className="text-[#6B7280]">No user data available</p>
      </div>
    );
  }

  const { userGrowth } = data;

  const weekOverWeekGrowth = comparisonUtils.getWeekOverWeekComparison(
    userGrowth.this_week_signups,
    userGrowth.last_week_signups
  );

  const monthOverMonthGrowth = comparisonUtils.getMonthOverMonthComparison(
    userGrowth.this_month_signups,
    userGrowth.last_month_signups
  );

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-[#8CAFA8] to-[#7A9B94] p-6 rounded-[16px] text-white shadow-[0_2px_12px_rgba(140,175,168,0.3)]">
          <div className="flex items-center gap-3 mb-2">
            <Users size={24} />
            <h3 className="text-sm font-medium opacity-90">Total Users</h3>
          </div>
          <div className="text-4xl font-bold mb-2">
            {founderAnalyticsService.formatNumber(userGrowth.total_users)}
          </div>
          <p className="text-sm opacity-75">All time</p>
        </div>

        <div className="bg-white p-6 rounded-[16px] border border-gray-100 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
          <div className="flex items-center gap-3 mb-2">
            <UserPlus className="text-[#8CAFA8]" size={24} />
            <h3 className="text-sm font-medium text-[#6B7280]">New This Week</h3>
          </div>
          <div className="text-4xl font-bold text-[#1F2937] mb-2">
            {userGrowth.this_week_signups}
          </div>
          <div className={`text-sm ${comparisonUtils.getTrendColor(weekOverWeekGrowth.isPositive)}`}>
            {comparisonUtils.getTrendIcon(weekOverWeekGrowth.trend)}{' '}
            {comparisonUtils.formatChangePercent(weekOverWeekGrowth)} vs last week
          </div>
        </div>

        <div className="bg-white p-6 rounded-[16px] border border-gray-100 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
          <div className="flex items-center gap-3 mb-2">
            <Calendar className="text-[#8CAFA8]" size={24} />
            <h3 className="text-sm font-medium text-[#6B7280]">New This Month</h3>
          </div>
          <div className="text-4xl font-bold text-[#1F2937] mb-2">
            {userGrowth.this_month_signups}
          </div>
          <div className={`text-sm ${comparisonUtils.getTrendColor(monthOverMonthGrowth.isPositive)}`}>
            {comparisonUtils.getTrendIcon(monthOverMonthGrowth.trend)}{' '}
            {comparisonUtils.formatChangePercent(monthOverMonthGrowth)} vs last month
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-[16px] border border-gray-100 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
          <h3 className="text-lg font-semibold text-[#1F2937] mb-4 flex items-center gap-2">
            <TrendingUp size={20} className="text-[#8CAFA8]" />
            Week-over-Week Comparison
          </h3>
          <div className="space-y-4">
            <div className="p-4 bg-[#F9FAFB] rounded-[12px]">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-[#6B7280]">This Week</span>
                <span className="text-2xl font-bold text-[#1F2937]">{userGrowth.this_week_signups}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-[#6B7280]">Last Week</span>
                <span className="text-2xl font-bold text-[#6B7280]">{userGrowth.last_week_signups}</span>
              </div>
            </div>
            <div className={`p-4 rounded-[12px] ${comparisonUtils.getTrendBackgroundColor(weekOverWeekGrowth.isPositive)}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Growth</span>
                <span className={`text-xl font-bold ${comparisonUtils.getTrendColor(weekOverWeekGrowth.isPositive)}`}>
                  {comparisonUtils.formatChange(weekOverWeekGrowth)} users
                </span>
              </div>
              <div className={`text-sm mt-1 ${comparisonUtils.getTrendColor(weekOverWeekGrowth.isPositive)}`}>
                {comparisonUtils.formatChangePercent(weekOverWeekGrowth)} change
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-[16px] border border-gray-100 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
          <h3 className="text-lg font-semibold text-[#1F2937] mb-4 flex items-center gap-2">
            <Calendar size={20} className="text-[#8CAFA8]" />
            Month-over-Month Comparison
          </h3>
          <div className="space-y-4">
            <div className="p-4 bg-[#F9FAFB] rounded-[12px]">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-[#6B7280]">This Month</span>
                <span className="text-2xl font-bold text-[#1F2937]">{userGrowth.this_month_signups}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-[#6B7280]">Last Month</span>
                <span className="text-2xl font-bold text-[#6B7280]">{userGrowth.last_month_signups}</span>
              </div>
            </div>
            <div className={`p-4 rounded-[12px] ${comparisonUtils.getTrendBackgroundColor(monthOverMonthGrowth.isPositive)}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Growth</span>
                <span className={`text-xl font-bold ${comparisonUtils.getTrendColor(monthOverMonthGrowth.isPositive)}`}>
                  {comparisonUtils.formatChange(monthOverMonthGrowth)} users
                </span>
              </div>
              <div className={`text-sm mt-1 ${comparisonUtils.getTrendColor(monthOverMonthGrowth.isPositive)}`}>
                {comparisonUtils.formatChangePercent(monthOverMonthGrowth)} change
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white p-6 rounded-[16px] border border-gray-100 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
        <h3 className="text-lg font-semibold text-[#1F2937] mb-4">Daily Signups</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-[#E8F2F0] rounded-[12px]">
            <div className="text-3xl font-bold text-[#8CAFA8]">{userGrowth.today_signups}</div>
            <div className="text-sm text-[#6B7280] mt-1">Today</div>
          </div>
          <div className="text-center p-4 bg-[#F9FAFB] rounded-[12px]">
            <div className="text-3xl font-bold text-[#1F2937]">{userGrowth.this_week_signups}</div>
            <div className="text-sm text-[#6B7280] mt-1">This Week</div>
          </div>
          <div className="text-center p-4 bg-[#F9FAFB] rounded-[12px]">
            <div className="text-3xl font-bold text-[#1F2937]">{userGrowth.this_month_signups}</div>
            <div className="text-sm text-[#6B7280] mt-1">This Month</div>
          </div>
          <div className="text-center p-4 bg-[#F9FAFB] rounded-[12px]">
            <div className="text-3xl font-bold text-[#1F2937]">{userGrowth.total_users}</div>
            <div className="text-sm text-[#6B7280] mt-1">All Time</div>
          </div>
        </div>
      </div>

      <div className="bg-[#E8F2F0] border border-[#8CAFA8]/30 p-6 rounded-[16px]">
        <h4 className="font-medium text-[#1F2937] mb-2">Growth Insights</h4>
        <ul className="space-y-2 text-sm text-[#374151]">
          {weekOverWeekGrowth.isPositive ? (
            <li>User growth is trending upward week-over-week ({comparisonUtils.formatChangePercent(weekOverWeekGrowth)})</li>
          ) : (
            <li>User growth has slowed compared to last week ({comparisonUtils.formatChangePercent(weekOverWeekGrowth)})</li>
          )}
          {monthOverMonthGrowth.isPositive ? (
            <li>Monthly user acquisition is improving ({comparisonUtils.formatChangePercent(monthOverMonthGrowth)})</li>
          ) : (
            <li>Monthly signups are down from last month ({comparisonUtils.formatChangePercent(monthOverMonthGrowth)})</li>
          )}
          <li>Average signups per day this week: {Math.round(userGrowth.this_week_signups / 7)}</li>
        </ul>
      </div>
    </div>
  );
}
